import { useEffect, useLayoutEffect, useRef } from 'react';

import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import {
  attachClosestEdge,
  extractClosestEdge
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

import type { SlotDropDelegate } from '../columnSlots';
import {
  chooseDelegate,
  DROP_SLOT_ATTRIBUTE,
  isPlanningCardSource,
  SLOT_ELEMENT
} from '../columnSlots';
import { claimHighlight, releaseHighlight } from '../dragHighlight';

/**
 * Registers a card as a drop target for the two slots beside it: its top half
 * lands in the slot above, its bottom half in the slot below.
 *
 * This is what makes a column's whole height droppable. Before it, cards were
 * made pointer-transparent during a drag and only the slots could be hit, so a
 * release aimed at a card hit nothing and planned nothing.
 *
 * The card contributes no times of its own — it hands over the slot's payload
 * unchanged, because the slot above a card carries the end of the *previous*
 * card, not this card's start.
 *
 * @param delegates the slots either side, from `InnerListPragmatic`. A card
 * rendered anywhere else (roster cell, "to plan" list) has neither and
 * registers nothing.
 */
export const useCardSlotDropTarget = (
  delegates: { above?: SlotDropDelegate; below?: SlotDropDelegate },
  isColumnLoading: boolean
) => {
  const itemRef = useRef<HTMLLIElement>(null);

  // Read inside `getData` rather than closed over: the slots around a card
  // change on every board update, and re-registering the target mid-drag would
  // drop the drag.
  const delegatesRef = useRef(delegates);
  useLayoutEffect(() => {
    delegatesRef.current = delegates;
  });

  const hasDelegates = !!(delegates.above || delegates.below);

  useEffect(() => {
    const el = itemRef.current;
    if (!el || !hasDelegates || isColumnLoading) return;

    // Resolved through the DOM rather than a second ref: the element only
    // decides where the indicator is drawn, so a slot that has meanwhile
    // stopped being a drop target degrades to no indicator instead of to a
    // drop on the wrong slot.
    const slotElement = (delegate?: SlotDropDelegate) =>
      delegate
        ? el.parentElement?.querySelector<HTMLElement>(
            `[${DROP_SLOT_ATTRIBUTE}="${CSS.escape(delegate.slotKey)}"]`
          ) ?? null
        : null;

    const highlightFromSelf = ({ self }: { self: { data: any } }) =>
      claimHighlight(el, (self.data[SLOT_ELEMENT] as HTMLElement) ?? null);

    const cleanup = dropTargetForElements({
      element: el,
      canDrop: ({ source }) => {
        const { above, below } = delegatesRef.current;
        // The card being dragged is deliberately *not* excluded. Refusing it
        // would not hand the drop to the slot overlapping it — pragmatic walks
        // ancestors, never siblings — so the bands where the neighbouring slots
        // reach into it would go dead instead, and that is exactly where a
        // multi-select drop into the gap beside the source card lands. Those
        // bands hit the slot underneath and planned before this change; letting
        // the source card resolve them to the same slot keeps that. A drop back
        // onto the card's own position is recognised later, by `isNoOpDrop`.
        return (
          isPlanningCardSource(source.data) &&
          // Without a slot to delegate to there is no moment to plan at, so
          // refuse rather than drop with an empty payload.
          !!(above || below)
        );
      },
      getData: ({ input, element }) => {
        const { above, below } = delegatesRef.current;
        const allowedEdges: Edge[] = [
          ...(above ? (['top'] as Edge[]) : []),
          ...(below ? (['bottom'] as Edge[]) : [])
        ];
        const edge = extractClosestEdge(
          attachClosestEdge({}, { input, element, allowedEdges })
        );
        const delegate = chooseDelegate(edge, above, below);

        return {
          ...delegate?.data,
          [SLOT_ELEMENT]: slotElement(delegate)
        };
      },
      // Both, not just `onDrag`: pragmatic dispatches a drop-target change as
      // leave-then-enter, and the `dragenter` listener publishes that change
      // *without* a following `onDrag` (only the `dragover` listener pairs the
      // two). Claiming on move alone would leave the indicator down from the
      // moment the previous slot released it until the next `dragover` — which,
      // if the pointer comes to rest on the boundary, is a visible gap.
      onDragEnter: highlightFromSelf,
      // Still needed on move: the delegate flips at the card's midpoint without
      // any drop-target change, since the card stays the target throughout.
      onDrag: highlightFromSelf,
      onDragLeave: () => releaseHighlight(el),
      onDrop: () => releaseHighlight(el)
    });

    return () => {
      cleanup();
      releaseHighlight(el);
    };
  }, [hasDelegates, isColumnLoading]);

  return itemRef;
};
